import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { cn } from "@/lib/utils"


declare global {
    const wvcClient: any;
}

/** -------- Types -------- */
export interface EcommerceTerm {
    id: string;
    name: string;
    slug?: string;
    taxonomy: string; // e.g., "product_cat" or "product_tag"
    link?: string;
    image_url?: string;
}


type TermQueryParams = Record<string, any>;

interface EcommerceTermsContextType {
    // data
    terms: EcommerceTerm[];
    total: number;
    totalPages: number;

    // status
    loading: boolean;
    isRefetching: boolean;
    error: string | null;

    // current query state (editable from children)
    query: TermQueryParams;
    embeds: string[];
    fields: string[];

    /**
     * Mutation helpers (children can drive queries)
     *
     * - setQuery: merges into the existing query by default (update mode). Pass a function to fully replace.
     * - setEmbeds: replaces the entire array by default (reset mode). Pass a function to update/merge manually.
     * - setFields: same as setEmbeds — replace by default, update if you pass a function.
     */
    setQuery: (next: Partial<TermQueryParams> | ((prev: TermQueryParams) => TermQueryParams)) => void;
    setEmbeds: (next: string[] | ((prev: string[]) => string[])) => void;
    setFields: (next: string[] | ((prev: string[]) => string[])) => void;

    // pagination helpers
    hasNext: boolean;
    hasPrev: boolean;
    setPage: (page: number) => void;
    nextPage: () => void;
    prevPage: () => void;

    // network
    refetch: () => Promise<void>;
    fetch: (opts?: {
        wp_term_query?: Partial<TermQueryParams>;
        embeds?: string[];
        fields?: string[];
        append?: boolean;
        refresh?: boolean; // force isRefresh mode
    }) => Promise<void>;
}

// Convert a single WP REST term (with optional _embedded) into our Term type
const normalizeEcommerceTerm = (raw: any): EcommerceTerm => {
    const name =
        raw?.name?.rendered ?? raw?.name ?? raw?.post_title ?? String(raw?.name || "Untitled");


    const link = raw?.resolved_url || raw?.link || raw?.url || undefined;

    const image_url =
        raw?._embedded?.["wp:image"]?.[0]?.source_url ??
        raw?.image_url ??
        undefined;

    return {
        id: String(raw?.id ?? raw?.ID),
        name: String(name),
        slug: raw?.slug,
        taxonomy: String(raw?.taxonomy ?? ""),
        image_url: raw?.image_url,
        link: raw?.link,
    };
};

export const convertWPResponseToEcommerceTerms = (input: any): EcommerceTerm[] => {
    if (!Array.isArray(input)) return [];
    return input.map(normalizeEcommerceTerm);
};

const EcommerceTermsContext = createContext<EcommerceTermsContextType | null>(null);

// hack to return the products as a property, instead of posts property for consumers
export const useEcommerceTerms = (): EcommerceTermsContextType & { terms: EcommerceTerm[] } => {
    const ctx = useContext(EcommerceTermsContext);
    if (!ctx) throw new Error("useEcommerceTerms must be used within a WordPressEcommerceTermsProvider");
    return {
        ...ctx,
        terms: ctx.terms,
    };
};

type WordPressEcommerceTermsProviderProps = {
    children: React.ReactNode;
    taxonomy?: string;      // taxonomy to fetch (defaults to 'product_cat')
    wp_term_query?: TermQueryParams; // initial query args
    embeds?: string[];      // initial embeds
    fields?: string[];      // initial fields
} & React.HTMLAttributes<HTMLDivElement>;

const WordPressEcommerceTermsProvider = ({
    children,
    taxonomy = 'product_cat', // default to product categories
    wp_term_query = {},
    embeds = [],
    fields = [],
    ...divProps
}: WordPressEcommerceTermsProviderProps) => {
    // Editable query state (seeded from props)
    const [termQuery, setTermQueryState] = useState<TermQueryParams>(wp_term_query);
    const [embedsState, setEmbedsState] = useState<string[]>(embeds);
    const [fieldsState, setFieldsState] = useState<string[]>(fields);

    // Data + status
    const [terms, setTerms] = useState<EcommerceTerm[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isRefetching, setIsRefetching] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    // Sync state if parent props change (treat props as "initial but updatable")
    useEffect(() => {
        setTermQueryState(wp_term_query || {});
    }, [JSON.stringify(wp_term_query)]);

    useEffect(() => {
        setEmbedsState(embeds || []);
    }, [JSON.stringify(embeds)]);

    const handleError = useCallback((e?: unknown) => {
        const msg = (e as any)?.message ?? String(e);
        setError(msg);
    }, []);

    const effectiveDeps = [
        JSON.stringify(termQuery),
        JSON.stringify(embedsState),
        JSON.stringify(fieldsState),
    ];

    const fetch = (opts?: {
        wp_term_query?: Partial<TermQueryParams>;
        embeds?: string[];
        fields?: string[];
        append?: boolean;
        refresh?: boolean;
    }): Promise<void> => {
        const isRefresh = Boolean(opts?.refresh);
        if (isRefresh && isRefetching) return Promise.resolve();

        setError(null);
        if (isRefresh) setIsRefetching(true);
        else setLoading(true);

        const mergedQuery: TermQueryParams = {
            ...termQuery,
            ...(opts?.wp_term_query || {}),
        };
        const usedEmbeds = opts?.embeds ?? embedsState;
        const usedFields = opts?.fields ?? fieldsState;

        return wvcClient?.getTerms?.({
            taxonomy: taxonomy,
            wp_term_query: mergedQuery,
            embeds: usedEmbeds,
            fields: usedFields,
        }).then((res: any) => {
            const normalized = (res?.terms ?? []).map(normalizeEcommerceTerm);
            setTerms(prev => (opts?.append ? [...prev, ...normalized] : normalized));
            setTotal(Number(res?.total ?? 0));
            setTotalPages(Number(res?.total_pages ?? 0));
        }).catch((e: any) => {
            handleError(e);
        }).finally(() => {
            if (isRefresh) setIsRefetching(false);
            else setLoading(false);
        });
    };

    // Initial + reactive fetch when query knobs change
    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, effectiveDeps);

    const refetch = (): Promise<void> => fetch({ refresh: true });

    // Expose convenient setters to children
    const setQuery: EcommerceTermsContextType["setQuery"] = (next) => {
        setTermQueryState(prev => (typeof next === "function" ? (next as any)(prev) : { ...prev, ...next }));
    };

    const setEmbeds: EcommerceTermsContextType["setEmbeds"] = (next) => {
        setEmbedsState(prev => (typeof next === "function" ? (next as any)(prev) : next));
    };

    const setFields: EcommerceTermsContextType["setFields"] = (next) => {
        setFieldsState(prev => (typeof next === "function" ? (next as any)(prev) : next));
    };

    // Pagination helpers derived from current query & totals
    const currentPage = Number(termQuery?.page ?? 1) || 1;
    const hasNext = currentPage < (totalPages || 0);
    const hasPrev = currentPage > 1;

    const setPage = (page: number) => setQuery({ page: Math.max(1, Math.floor(page || 1)) });
    const nextPage = () => hasNext && setPage(currentPage + 1);
    const prevPage = () => hasPrev && setPage(currentPage - 1);

    const value: EcommerceTermsContextType = useMemo(() => ({
        // data
        terms,
        total,
        totalPages,

        // status
        loading,
        isRefetching,
        error,

        // state
        termQuery,
        embeds: embedsState,
        fields: fieldsState,

        // setters
        setQuery,
        setEmbeds,
        setFields,

        // pagination
        hasNext,
        hasPrev,
        setPage,
        nextPage,
        prevPage,

        // network
        refetch,
        fetch,
    }), [
        terms,
        total,
        totalPages,
        loading,
        isRefetching,
        error,
        termQuery,
        embedsState,
        fieldsState,
        hasNext,
        hasPrev,
    ]);

    return (
        <EcommerceTermsContext.Provider value={value}>
            <div
                {...divProps}
                data-wvc-dynamic="EcommerceTermsProvider"
                data-wvc-wp_term_query={JSON.stringify(wp_term_query)}
                data-wvc-embeds={JSON.stringify(embeds)}
                data-wvc-fields={JSON.stringify(fields)}
            >
                {children}
            </div>
        </EcommerceTermsContext.Provider>
    );
};

export { WordPressEcommerceTermsProvider, WordPressEcommerceTermsProvider as EcommerceTermsProvider };

